import { useEffect, useRef, useState } from 'react'
import type { GameState, ScoreBreakdown } from '../game/types.ts'
import { LINE_SCORE_TABLE, BONUS_PERCENT } from '../config/scoring.ts'
import block1 from '../assets/block1.webp'
import block2 from '../assets/block2.webp'
import { LeaderboardPanel } from './LeaderboardPanel.tsx'
import { StaminaDisplay } from './StaminaDisplay.tsx'
import { useStamina } from '../hooks/useStamina.ts'
import { submitVerifiedGameScore } from '../services/scores.ts'
import { invalidateLeaderboardCache } from '../services/leaderboard.ts'
import { trackEvent } from '../services/analytics.ts'
import { t } from '../i18n/translations.ts'

interface Props {
  state: GameState
  blackScore: ScoreBreakdown
  whiteScore: ScoreBreakdown
  highscore: number
  isNewHighscore: boolean
  onRetry: () => void
  onTitle: () => void
}

function iconForColor(color: 'black' | 'white'): string {
  return color === 'black' ? block1 : block2
}

function ScoreTable({
  breakdown,
  label,
  icon,
  hasKomi,
}: {
  breakdown: ScoreBreakdown
  label: string
  icon: string
  hasKomi: boolean
}) {
  const lineEntries = Object.entries(breakdown.lineCounts)
    .map(([k, v]) => ({ len: Number(k), count: v }))
    .sort((a, b) => b.len - a.len)

  return (
    <div className="score-table">
      <div className="score-table__header">
        <img src={icon} alt={label} className="score-table__icon" />
        <span className="score-table__name">{label}</span>
        {hasKomi && (
          <span className="score-table__komi">+{BONUS_PERCENT}%</span>
        )}
        <span className="score-table__total">
          {Math.round(breakdown.total).toLocaleString()}pt
        </span>
      </div>
      <div className="score-table__rows">
        {lineEntries.map(({ len, count }) => {
          const pts = LINE_SCORE_TABLE[len] ?? 0
          return (
            <div key={len} className="score-table__row">
              <span>
                {len}連 ×{count}
              </span>
              <span>+{(pts * count).toLocaleString()}</span>
            </div>
          )
        })}
        {breakdown.clusterBonus > 0 && (
          <div className="score-table__row">
            <span>{t('clusterBonus')}</span>
            <span>+{Math.round(breakdown.clusterBonus).toLocaleString()}</span>
          </div>
        )}
        {lineEntries.length === 0 && breakdown.clusterBonus === 0 && (
          <div className="score-table__row score-table__row--empty">
            <span>{t('scoreNone')}</span>
            <span>0</span>
          </div>
        )}
      </div>
    </div>
  )
}

export function ResultScreen({
  state,
  blackScore,
  whiteScore,
  highscore,
  isNewHighscore,
  onRetry,
  onTitle,
}: Props) {
  const playerColor = state.playerColor
  const cpuColor: 'black' | 'white' = playerColor === 'black' ? 'white' : 'black'
  const playerScore = playerColor === 'black' ? blackScore : whiteScore
  const playerWon = state.winner === playerColor
  const isFiveRow = state.winLine !== null
  const submittedScore = Math.round(playerScore.total)

  const { canPlay, consume } = useStamina()

  // 同じ submittedScore が複数回届いた場合の再送信を防ぐ（React Strict Mode / remount 対策）。
  // web_guest は Hub 側で 403 GUEST_NOT_ALLOWED になるため、現状はサイレントに失敗する。
  // 将来 web_user / android/ios 化時に、UI に送信状態バッジを追加する前提。
  //
  // 重要: `sentScoreRef.current` は「送信成功時のみ」更新する。送信前や失敗時に先に
  // セットすると、transient failure（5xx / ネットワーク一時断）でも「送信済み」扱いになり
  // リトライが発生しなくなる。ガード用に in-flight 判定 ref も別途持つ。
  const sentScoreRef = useRef<number | null>(null)
  const inflightScoreRef = useRef<number | null>(null)
  // スコア送信成功後に LeaderboardPanel を再フェッチさせるためのトリガー。
  // invalidateLeaderboardCache() だけでは Panel 内の useEffect が再実行されず、
  // 「自分のハイスコアがリザルトのランキングに反映されない」状態になる。
  const [leaderboardRefreshKey, setLeaderboardRefreshKey] = useState(0)
  useEffect(() => {
    if (sentScoreRef.current === submittedScore) return
    if (inflightScoreRef.current === submittedScore) return
    inflightScoreRef.current = submittedScore
    void submitVerifiedGameScore({
      score: submittedScore,
      sessionVerification: state.sessionVerification,
    })
      .then((ok) => {
        // failed = 全 period が失敗またはリトライキュー行き（web_guest の 403 を含む）。
        trackEvent('score_submit', { status: ok ? 'success' : 'failed' })
        if (ok) {
          sentScoreRef.current = submittedScore
          invalidateLeaderboardCache()
          setLeaderboardRefreshKey((k) => k + 1)
        }
      })
      .catch((err) => {
        trackEvent('score_submit', { status: 'reauth_required' })
        // submitGameScore は 401（要再認証）のみ throw する。notifyReauthRequired は
        // 内部で発火済みなので App.tsx のエラー画面に誘導される。ここでは追加処理不要。
        console.warn('[ResultScreen] submitGameScore reauth required:', err)
      })
      .finally(() => {
        if (inflightScoreRef.current === submittedScore) {
          inflightScoreRef.current = null
        }
      })
  }, [submittedScore, state.sessionVerification])

  const winnerMessage = (() => {
    if (playerWon) {
      return isFiveRow
        ? t('resultWinFive')
        : t('resultWinScore')
    }
    if (state.winner !== null) {
      return isFiveRow ? t('resultLoseFive') : t('resultLoseScore')
    }
    return t('resultDraw')
  })()

  const playerHasKomi = playerColor === 'white'
  const cpuHasKomi = cpuColor === 'white'

  const handleRetry = () => {
    if (!canPlay) return
    const consumed = consume('npc')
    if (consumed) onRetry()
  }

  return (
    <div className="screen screen--result">
      <div className="result-overlay">
        <h2 className="result-winner">{winnerMessage}</h2>

        {/* 花畑演出（プレイヤー勝利時） */}
        {playerWon && (
          <div className="result-flowers">{'🌸'.repeat(8)}</div>
        )}

        {isNewHighscore && (
          <div className="result-new-highscore">{t('newHighscore')}</div>
        )}

        {/* 両者のスコア内訳（プレイヤーを左、CPU を右） */}
        <div className="result-scores">
          <ScoreTable
            breakdown={playerColor === 'black' ? blackScore : whiteScore}
            label={t('you')}
            icon={iconForColor(playerColor)}
            hasKomi={playerHasKomi}
          />
          <ScoreTable
            breakdown={cpuColor === 'black' ? blackScore : whiteScore}
            label="CPU"
            icon={iconForColor(cpuColor)}
            hasKomi={cpuHasKomi}
          />
        </div>

        {/* ハイスコア表示 */}
        <div className="result-highscore">
          {t('highscore')}:{' '}
          {Math.max(submittedScore, highscore).toLocaleString()}
          pt
        </div>

        {/* みんなの戦績タイムライン */}
        <LeaderboardPanel
          period="daily"
          limit={3}
          refreshKey={leaderboardRefreshKey}
        />

        {/* ライフ（もういちど のコスト表示） */}
        <StaminaDisplay showRecoverButton />

        <div className="result-buttons">
          <button
            className="btn btn--primary btn--large"
            onClick={handleRetry}
            disabled={!canPlay}
          >
            {canPlay ? t('retry') : t('staminaEmpty')}
          </button>
          <button className="btn btn--secondary" onClick={onTitle}>
            {t('titleBack')}
          </button>
        </div>
      </div>
    </div>
  )
}
